import { useCallback, useRef } from 'react';
import React from 'react';
import { LayerHandleV1, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';

import { GarageLayerData, useGarageLayerData } from '../../components/GarageLayerContent/hooks/useGarageLayerData';
import { LayerContentWrapper } from '../../../../shared/components/layer-content-wrapper/LayerContentWrapper';
import AddVehicleView, { AddVehicleViewProps } from '../../components/add-vehicle/AddVehicleView';
import { BRIDGE_KEYS, LayerDataBridge } from '../../../../shared/utils/layer-data-bridge';
import { useLayerManager } from '../../../../shared/hooks/use-layer-manager';

type GarageLayerRenderFn = (
  state: { dataBridge: LayerDataBridge<GarageLayerData> },
  close: any,
) => JSX.Element;

export const useGarageLayer = (active: boolean, onClose: () => void, renderFn: GarageLayerRenderFn) => {
  const layerManager = useLayerManager();
  const hasOpenedRef = useRef(false);
  const layerHandle = useRef<LayerHandleV1<{ dataBridge: LayerDataBridge<GarageLayerData> }> | null>(null);
  const bridge = LayerDataBridge.getInstance<GarageLayerData>(BRIDGE_KEYS.GLOBAL);

  const openAddVehicleLayer = useCallback(
    (data: AddVehicleViewProps) => {
      layerManager?.openInteractionLayer(
        (state: AddVehicleViewProps, close: any) => (
          <LayerContentWrapper
            title={state.translationHandler(
              'vehicleManagement.interaction.layer.add.vehicle.title',
            )}
            onClose={() => close()}
          >
            <AddVehicleView {...state} />
          </LayerContentWrapper>
        ),
        data,
        { size: 'E' as any },
      );
    },
    [layerManager],
  );

  const closeGarageLayer = useCallback(() => {
    layerHandle.current?.close();
  }, []);

  const garageData = useGarageLayerData({
    onCloseLayer: closeGarageLayer,
    onAddVehicle: openAddVehicleLayer,
  });

  React.useEffect(() => {
    if (!active) return;
    bridge.setData(garageData);
  }, [active, garageData]);

  React.useEffect(() => {
    if (active && !hasOpenedRef.current && layerManager) {
      hasOpenedRef.current = true;
      bridge.setData(garageData);
      layerHandle.current = layerManager.openInteractionLayer(
        renderFn,
        { dataBridge: bridge },
        {
          size: 'E' as any,
          shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
          onClose: (_state: any, _response: any) => {
            hasOpenedRef.current = false;
            bridge.setData(null);
            onClose();
          },
        },
      );
    }
    if (!active) {
      hasOpenedRef.current = false;
    }
  }, [active]);
};
